/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import React, { useCallback, useMemo, useReducer } from 'react';
import { jsx } from '@emotion/react';
import { Tooltip } from '@blueprintjs/core';
import { directionalProperty } from 'polished';

interface Location {
	x: number;
	y: number;
}

export interface State {
	data;
	location: Location;
	options;
}

export interface TooltipReturn {
	TooltipElement: JSX.Element;
	updateTooltip: (data: any, location: Location, options: any) => void;
}

type Action = { location; data; options };

// use a reducer to reduce the number of renders
function reducer(state: State, action: Action): State {
	if (action === null) {
		return {
			data: null,
			location: null,
			options: null,
		};
	}

	return {
		...state,
		data: action.data,
		location: action.location,
		options: action.options,
	};
}

/**
 * Common tooltip framework for "virtual" element.  For example, an SVG based chart that has a bunch of elements that
 * need individual tooltips.
 *
 * @param renderer
 * @returns {{setTooltipData: React.Dispatch<React.SetStateAction<unknown>>, setTooltipOptions: React.Dispatch<React.SetStateAction<unknown>>, TooltipElement: unknown, setTooltipLocation: React.Dispatch<React.SetStateAction<unknown>>}}
 */
export function useVirtualTooltip(renderer: (args: any) => JSX.Element): TooltipReturn {
	const [state, dispatch] = useReducer(reducer, { location: null, data: null, options: null });

	const updateTooltip = useCallback(
		(data: any, location: Location, options: any) => {
			dispatch({ data, location, options });
		},
		[dispatch]
	);

	const TooltipElement = useMemo(() => {
		if (!state.location) return null;

		return (
			<div
				style={{
					position: 'absolute',
					left: state.location.x,
					top: state.location.y,
					height: 0,
					width: 0,
					display: 'flex',
					pointerEvents: 'none',
				}}
			>
				<Tooltip boundary={'window'} content={renderer(state.data)} isOpen={state.location.x > 0} {...state.options} targetTagName={"div"}>
					<div />
				</Tooltip>
			</div>
		);
	}, [renderer, state.data, state.location, state.options]);

	return {
		TooltipElement,
		updateTooltip,
	};
}
